import React, { ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useRequest, useThrottleFn } from 'ahooks'
import {
  ICoursewareModalContexts,
  useCoursewareModal,
} from '@/hooks/myPages/CoursewareModalProvider'
import { VideoUploadForm } from '@/components/Form/Courseware/VideoUploadForm'
import { submitClassWorkApi, submitWorkApiV2 } from '@/services/class'
import { PDFUploadForm } from '@/components/Form/Courseware/PDFUploadForm'
import useRawContent from '@/hooks/useRawContent'
import ReplayCodeUploadForm from '@/components/Form/Courseware/ReplayCodeUploadForm'
import ReplayFileUploadForm from '@/components/Form/Courseware/ReplayFileUploadForm'

type ITeamScrimUploadProps = {
  header?: ReactNode
  classId: number
  nodeId: number
  onSuccess: () => void
  ctx?: ICoursewareModalContexts
}

export const TeamScrimUpload = ({
  header,
  classId,
  onSuccess,
  nodeId,
  ctx = 'CLASS_MODAL_CONTEXT',
}: ITeamScrimUploadProps) => {
  const { courseware, requestClose, setBeforeClose } = useCoursewareModal(ctx)

  const { parsed, renderReplacedVideo } = useRawContent({
    content: courseware.content || '',
  })

  // 2026-09 이관: 업로드가 전부 S3 로 가므로 언어와 무관하게 `aws` 입니다.
  //   예전에는 한국만 S3 였고 나머지는 알리바바 OSS 라 언어로 갈랐습니다.
  const platform = 'aws'

  const _onSubmit = useCallback(
    async data => {
      // Build POST Body Data
      const postData = {
        description: data.description,
        content: data.url,
        file_path: data.file_name,
        replay_code: data.replay_code,
        replay_file_path: data.replay_file_path,
        file_upload_platform: data.file_upload_platform || platform,
        file_size: data.file_size,
        player_identity: data.player_identity,
      }

      await submitClassWorkApi(classId, nodeId, postData)
      await submitWorkApiV2(classId, nodeId)
      onSuccess()
    },
    [classId, nodeId, onSuccess, platform]
  )

  const { runAsync: onSubmit, loading: submitting } = useRequest(_onSubmit, {
    manual: true,
  })

  const { run: onSubmitThrottle } = useThrottleFn(onSubmit, { wait: 1000 })

  const submissionType = useMemo(() => {
    return courseware.syllabus.submit_type
  }, [courseware])

  useEffect(() => {
    renderReplacedVideo()
  }, [renderReplacedVideo])

  return (
    <div
      className={
        "overflow-auto bg-repeat bg-[url('https://res.ggacademy.gg/user-web/images/bg-faq.png')]"
      }
    >
      <div className={'max-w-screen-xl mx-auto bg-white bg-opacity-60 p-2'}>
        {header}
        <div
          className="w-full gga-page-view"
          dangerouslySetInnerHTML={{ __html: parsed }}
        />
        {submissionType === 'video' && (
          <VideoUploadForm
            submitting={submitting}
            requestClose={requestClose}
            onSubmit={onSubmitThrottle}
            onModalClose={setBeforeClose}
          />
        )}

        {submissionType === 'replay_code' && (
          <ReplayCodeUploadForm
            onSubmit={onSubmitThrottle}
            onModalClose={setBeforeClose}
          />
        )}
        {submissionType === 'replay_file' && (
          <ReplayFileUploadForm
            onSubmit={onSubmitThrottle}
            onModalClose={setBeforeClose}
          />
        )}

        {submissionType === 'pdf' && (
          <PDFUploadForm
            submitting={submitting}
            requestClose={requestClose}
            onSubmit={onSubmitThrottle}
            onModalClose={setBeforeClose}
          />
        )}
      </div>
    </div>
  )
}
